import { ModalBlock } from './supabase/modalContent';

// Look Deeper types
export interface NarrativeSection {
  id: string;
  title: string;
  body: string;
  locked?: boolean;
}

export interface LookDeeperContent {
  narrativeSections: NarrativeSection[];
}

// Parse legacy format - extract only "What It's Really About" section
function parseLegacyNarrativeSections(text: string, blockId: string): NarrativeSection[] {
  const sections: NarrativeSection[] = [];

  if (!text || text.trim().length === 0) {
    return sections;
  }

  // Try to extract "What It's Really About" section
  const whatItsReallyAboutMatch = text.match(/What It's Really About[\s\n]+([\s\S]+)$/);

  if (whatItsReallyAboutMatch && whatItsReallyAboutMatch[1]) {
    sections.push({
      id: `${blockId}-what-its-really-about`,
      title: "What It's Really About",
      body: whatItsReallyAboutMatch[1].trim(),
      locked: true,
    });
  } else if (text.includes("What It's Really About")) {
    // Header exists but regex didn't match - try simple split
    const parts = text.split("What It's Really About");
    if (parts.length > 1 && parts[1].trim()) {
      sections.push({
        id: `${blockId}-what-its-really-about`,
        title: "What It's Really About",
        body: parts[1].trim(),
        locked: true,
      });
    }
  } else {
    // No header found - use all text as "What It's Really About"
    sections.push({
      id: `${blockId}-what-its-really-about`,
      title: "What It's Really About",
      body: text.trim(),
      locked: true,
    });
  }

  return sections;
}

// Convert ModalBlocks to Look Deeper structured content
export function modalBlocksToLookDeeper(blocks: ModalBlock[]): LookDeeperContent {
  const content: LookDeeperContent = {
    narrativeSections: [],
  };

  for (const block of blocks) {
    if (block.block_type === 'short_text') {
      const bodyText = block.body || '';

      // Check if this is legacy data with "What It's Really About" as a heading in the body
      if (!block.title && bodyText.includes("What It's Really About")) {
        // Parse the legacy text to extract "What It's Really About" section
        const parsed = parseLegacyNarrativeSections(bodyText, block.id);
        content.narrativeSections.push(...parsed);
      } else if (block.title === "What It's Really About") {
        // Normal case: already has proper title
        content.narrativeSections.push({
          id: block.id,
          title: block.title,
          body: bodyText,
          locked: true,
        });
      } else if (block.title && block.title !== 'Behind the Song') {
        // Additional section (not the fixed section, not the old removed section)
        content.narrativeSections.push({
          id: block.id,
          title: block.title,
          body: bodyText,
          locked: false,
        });
      }
      // Skip "Behind the Song" sections completely
    }
    // Ignore timeline blocks - no longer supported for Observer level
  }

  // Ensure "What It's Really About" section exists
  const whatItsReallyAboutIndex = content.narrativeSections.findIndex(s => s.title === "What It's Really About");

  if (whatItsReallyAboutIndex === -1) {
    // Look for a section with empty title that has content
    const firstSectionIndex = content.narrativeSections.findIndex(s => !s.title.trim() && s.body.trim());
    if (firstSectionIndex !== -1) {
      // Use this as "What It's Really About"
      content.narrativeSections[firstSectionIndex].title = "What It's Really About";
      content.narrativeSections[firstSectionIndex].locked = true;
    } else {
      // Create new empty "What It's Really About"
      content.narrativeSections.unshift({
        id: 'what-its-really-about',
        title: "What It's Really About",
        body: '',
        locked: true,
      });
    }
  } else if (whatItsReallyAboutIndex > 0) {
    // Ensure "What It's Really About" is first
    const [whatItsReallyAbout] = content.narrativeSections.splice(whatItsReallyAboutIndex, 1);
    content.narrativeSections.unshift(whatItsReallyAbout);
  }

  return content;
}

// Convert Look Deeper structured content to ModalBlocks
export function lookDeeperToModalBlocks(content: LookDeeperContent): Partial<ModalBlock>[] {
  const blocks: Partial<ModalBlock>[] = [];

  // Add narrative sections, filtering out any malformed additional sections and "Behind the Song"
  content.narrativeSections.forEach((section, index) => {
    // Skip "Behind the Song" section completely
    if (section.title === 'Behind the Song') {
      return;
    }

    const isFixedSection = section.title === "What It's Really About";
    const trimmedTitle = section.title.trim();
    const trimmedBody = section.body.trim();

    // Skip if: not a fixed section, title is empty, and body is also empty
    if (!isFixedSection && !trimmedTitle && !trimmedBody) {
      return; // Skip empty additional sections
    }

    blocks.push({
      id: section.id && !section.id.startsWith('section-') ? section.id : undefined,
      block_type: 'short_text',
      title: section.title,
      body: section.body,
      url: null,
      xp_value: 0,
      metadata: {},
      enabled: true,
      order_index: index,
    });
  });

  return blocks;
}

// One Key Insight types
export interface OneKeyInsightContent {
  insight?: string;
  chips?: Array<{ id: string; text: string }>;
}

export function modalBlocksToOneKeyInsight(blocks: ModalBlock[]): OneKeyInsightContent {
  const block = blocks[0];

  // Check if this is insight_chips format (Explorer level)
  if (block?.block_type === 'insight_chips' && block.metadata?.chips) {
    return {
      chips: block.metadata.chips,
    };
  }

  // Default: single insight text
  return {
    insight: block?.body || '',
  };
}

export function oneKeyInsightToModalBlocks(content: OneKeyInsightContent): Partial<ModalBlock>[] {
  // If chips exist (Explorer level), return insight_chips block
  if (content.chips && content.chips.length > 0) {
    return [
      {
        block_type: 'insight_chips',
        title: null,
        body: null,
        url: null,
        xp_value: 0,
        metadata: { chips: content.chips },
      },
    ];
  }

  // Default: single insight text
  if (!content.insight?.trim()) return [];

  return [
    {
      block_type: 'fact',
      title: null,
      body: content.insight,
      url: null,
      xp_value: 0,
      metadata: {},
      enabled: true,
      order_index: 0,
    },
  ];
}

// Explore Connections types
export interface ExploreConnectionsContent {
  relatedSongIds: string[];
}

export function modalBlocksToExploreConnections(blocks: ModalBlock[]): ExploreConnectionsContent {
  const block = blocks[0];
  const songIds = block?.metadata?.song_ids || [];
  return {
    relatedSongIds: Array.isArray(songIds) ? songIds : [],
  };
}

export function exploreConnectionsToModalBlocks(
  content: ExploreConnectionsContent
): Partial<ModalBlock>[] {
  if (content.relatedSongIds.length === 0) return [];

  return [
    {
      block_type: 'related_songs',
      title: null,
      body: null,
      url: null,
      xp_value: 0,
      metadata: { song_ids: content.relatedSongIds },
      enabled: true,
      order_index: 0,
    },
  ];
}

// Take It Further types
export interface TakeItFurtherContent {
  actionType: 'collect_only' | 'micro_action' | 'custom';
  customText?: string;
  microActionId?: string;
}

export function modalBlocksToTakeItFurther(blocks: ModalBlock[]): TakeItFurtherContent {
  const block = blocks[0];

  if (!block) {
    return { actionType: 'collect_only' };
  }

  const microActionId = block.metadata?.micro_action_id;
  if (microActionId) {
    return {
      actionType: 'micro_action',
      microActionId: microActionId,
    };
  }

  if (block.body) {
    return {
      actionType: 'custom',
      customText: block.body,
    };
  }

  return { actionType: 'collect_only' };
}

export function takeItFurtherToModalBlocks(content: TakeItFurtherContent): Partial<ModalBlock>[] {
  if (content.actionType === 'collect_only') {
    return [];
  }

  if (content.actionType === 'micro_action' && content.microActionId) {
    return [
      {
        block_type: 'cta_action',
        title: null,
        body: null,
        url: null,
        xp_value: 0,
        metadata: { micro_action_id: content.microActionId },
        enabled: true,
        order_index: 0,
      },
    ];
  }

  if (content.actionType === 'custom' && content.customText) {
    return [
      {
        block_type: 'short_text',
        title: null,
        body: content.customText,
        url: null,
        xp_value: 0,
        metadata: {},
        enabled: true,
        order_index: 0,
      },
    ];
  }

  return [];
}
